import { all, call, fork, put, select, takeLatest } from 'typed-redux-saga';

import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import logger from '@/core/observability/logger';
import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { authSelector } from '@/customerManagement/customerManagement.selectors';
import { customerProductPreferencesSelector } from '@/customerManagement/customerProductPreferences/selectors';
import { AlgoliaSearchInsights } from '@/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';
import { type CustomAlgoliaSearchResults } from '@/productDiscovery/Recommendation/interfaces/algolia';
import { type Recommendations } from '@/productDiscovery/Recommendation/interfaces/productRecommendation';
import {
  CONTENT_TYPE_MS_RECO,
  ERROR_MESSAGE,
} from '@/productDiscovery/Recommendation/modules/ProductRecommendation/constants';
import { type ProductRecommendationParams } from '@/productDiscovery/Recommendation/modules/ProductRecommendation/interfaces/ProductRecommendation.model';
import {
  TRIGGER_A2C_RECOS,
  TriggerA2CRecos,
} from '@/productDiscovery/Recommendation/modules/ProductRecommendation/ProductRecommendation.actions';
import { selectAbPool } from '@/productDiscovery/Recommendation/selectors/abPool';
import { processSearchResult } from '@/productDiscovery/Recommendation/services/algolia/processor';
import { algoliaSearchFromRecommendation } from '@/productDiscovery/Recommendation/services/algolia/proxy.client';
import { createPlaylistContainer } from '@/productDiscovery/Recommendation/services/algolia/utils/createPlaylistContainer/createPlaylistContainer';
import { fetchMsRecommendation } from '@/productDiscovery/Recommendation/services/msRecommendation/fetch.common';
import { processRecommendationResult } from '@/productDiscovery/Recommendation/services/msRecommendation/processors/processRecommendationResult';
import { SponsoredProductsLocationType } from '@/productDiscovery/SponsoredProducts/interfaces/SponsoredProducts';
import { reorderSponsoredProductsSaga } from '@/productDiscovery/SponsoredProducts/ReorderSponsoredProducts.saga';
import {
  A2C_FETCH_RECOMMENDATION,
  A2C_FETCH_RECOMMENDATION_CONFIG_SUCCESS,
  a2cFetchProductsRecommendation,
  a2cFetchProductsRecommendationFailed,
  a2cFetchProductsRecommendationSuccess,
  a2cFetchRecommendationConfig,
  a2cFetchRecommendationConfigFailed,
  a2cFetchRecommendationConfigSuccess,
  type A2CFetchRecommendation,
  type A2CFetchRecommendationConfigSuccess,
} from '@/shoppingCart/AddToCart/AddToCart.actions';
import { selectAddToCartState } from '@/shoppingCart/AddToCart/selectors/selectAddToCartState';

export function* addToCartRecommendationsSaga({ payload }: TriggerA2CRecos) {
  try {
    const { categoryId } = payload;

    const { products } = yield select(selectAddToCartState);
    yield put(a2cFetchRecommendationConfig());
    const fetchParams: ProductRecommendationParams = {
      pageType: 'add_to_cart_popin',
      modelId: products[0].pr_model_id || products[0].modelId,
      categoryId,
    };

    const selectFlagValuesMap = yield* call(waitForAndGetFlagValuesSaga);
    const abTestPool = yield* select(selectAbPool, selectFlagValuesMap);

    const response = yield* call(
      fetchMsRecommendation,
      fetchParams,
      abTestPool,
    );

    const processedData = processRecommendationResult(response);
    yield* put(a2cFetchRecommendationConfigSuccess(processedData));
  } catch (error) {
    logger.error(
      new Error('Error fetching product recommendation config in A2C Popin.'),
    );
    yield* put(a2cFetchRecommendationConfigFailed(error as Error));
  }
}

export function* addToCartGetProductsForRecommendation(
  action: A2CFetchRecommendationConfigSuccess | A2CFetchRecommendation,
) {
  try {
    yield* put(a2cFetchProductsRecommendation());
    if (action.config.length === 0) throw new Error(ERROR_MESSAGE.EMPTY_CONFIG);

    // Recommendations filtered by type (exclude unused types)
    const configFiltered = action.config.filter((reco) =>
      Object.values(CONTENT_TYPE_MS_RECO).includes(reco.displayType),
    );

    const { userId } = yield* select(authSelector);
    const visitorId = yield* call(getOrCreateVisitorId);
    const userToken = userId
      ? yield* call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const algoliaResults = (yield* call(
      algoliaSearchFromRecommendation,
      configFiltered,
      userToken,
    )) as CustomAlgoliaSearchResults[];

    const processedData = processSearchResult(algoliaResults);

    const { purchasedTwice } = yield* select(
      customerProductPreferencesSelector,
    );

    const playlists: Recommendations = createPlaylistContainer(
      configFiltered,
      processedData,
      purchasedTwice,
    );

    const newPlaylists: any = yield* call(
      // @ts-expect-error ProductDiscovery hasn't migrated to typed sagas yet
      reorderSponsoredProductsSaga,
      playlists,
      SponsoredProductsLocationType.ADD_TO_CART_PAGE,
    );

    yield* put(a2cFetchProductsRecommendationSuccess(newPlaylists));
  } catch (error) {
    logger.error(
      new Error('Error fetching product recommendation products in A2C Popin.'),
    );
    yield* put(a2cFetchProductsRecommendationFailed(error as Error));
  }
}

// WATCHER: to get products for recommendation
export function* watchFetchRecommendationConfigSuccess() {
  yield* takeLatest(
    [A2C_FETCH_RECOMMENDATION_CONFIG_SUCCESS, A2C_FETCH_RECOMMENDATION],
    addToCartGetProductsForRecommendation,
  );
}

// WATCHER: to initialize
export function* watchAddToCartRecommendationsSaga() {
  yield takeLatest(TRIGGER_A2C_RECOS, addToCartRecommendationsSaga);
}

export function* rootAddToCartRecommendationsSaga() {
  yield* all([
    fork(watchAddToCartRecommendationsSaga),
    fork(watchFetchRecommendationConfigSuccess),
  ]);
}
